/**
 * Every server the game uses to find a path between two browsers, in one list.
 *
 * STUN tells a browser what its own public address looks like, which is enough for most home
 * networks. TURN relays the traffic when it is not: symmetric NAT, carrier-grade NAT on mobile
 * data, and corporate networks that block UDP outright. A TURN server carries every packet of a
 * session that needs it, which is why free ones are rare.
 *
 * The honest situation, so nobody has to discover it in a bug report:
 *
 * - Open Relay is free, its credentials are published, and every user of the service shares them.
 *   They are not a secret and there is nothing here to leak.
 * - It is bandwidth-capped and best-effort. It can be slow, and it can be down.
 * - Some networks still will not connect. Two peers behind symmetric NAT with TURN unavailable have
 *   no path, and no amount of retrying invents one.
 * - When that happens the session fails with "Could not open a direct connection" inside the
 *   connection budget. It does not sit on a spinner, and it does not silently drop to a
 *   half-working state.
 *
 * The way out, when this project outgrows a shared free relay, is a paid or self-hosted TURN server
 * and a different list here. Nothing else in the codebase names an ICE server.
 */

export const ICE_SERVERS: readonly RTCIceServer[] = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
  { urls: ['stun:stun.cloudflare.com:3478'] },
  {
    urls: [
      'turn:openrelay.metered.ca:80',
      'turn:openrelay.metered.ca:443',
      'turns:openrelay.metered.ca:443?transport=tcp',
    ],
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
];

/**
 * Handed to Trystero as `rtcConfig`, which replaces its own defaults wholesale rather than adding
 * to them. That is deliberate: one list beats two lists that drift.
 */
export function rtcConfiguration(): RTCConfiguration {
  return { iceServers: [...ICE_SERVERS] };
}
